/**
 * IdleTimeoutManager
 *
 * Monitors user activity and automatically signs out after 60 minutes
 * of inactivity. Only active when the user is authenticated.
 *
 * On timeout: calls signOut(), displays a brief notification, and the
 * auth state change naturally redirects to the sign-in page.
 *
 * Requirements: 6.1, 6.3
 */

import { useCallback } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useIdleTimeout } from '@/hooks/useIdleTimeout';

const IDLE_TIMEOUT_MS = 60 * 60 * 1000; // 60 minutes

/**
 * Inner component that activates the idle timeout hook.
 * Only mounted when the user is authenticated.
 */
function IdleTimeoutListener() {
  const { signOut } = useAuth();

  const handleTimeout = useCallback(async () => {
    try {
      await signOut();
    } catch (err) {
      console.error('Failed to sign out on idle timeout:', err);
    }
    // Show notification after sign-out so the user sees it on the sign-in page
    alert('Session expired due to inactivity');
  }, [signOut]);

  useIdleTimeout({
    timeoutMs: IDLE_TIMEOUT_MS,
    onTimeout: handleTimeout,
  });

  return null;
}

/**
 * Conditionally renders the idle timeout listener only when
 * the user is authenticated. Must be placed inside AuthProvider.
 */
export function IdleTimeoutManager() {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading || !isAuthenticated) {
    return null;
  }

  return <IdleTimeoutListener />;
}
